import { useReducer, useEffect } from "react";
import { Interview } from "./Interview";
import { getFromLocalStorage } from "../utils/localStorageHelpers";
import * as reducers from "./useInterviewActions";

export interface State {
  interviewStarted: boolean;
  abandonInterviewRequested: boolean;
  interview?: Interview;
}

export type StartInterviewAction = {
  type: "startInterview";
  interview: Interview;
};
export type ChangeInterviewNameAction = {
  type: "changeInterviewName";
  interviewName: string;
};
export type AnswerQuestionAction = {
  type: "answerQuestion";
  actionParams: AnswerQuestionActionParams;
};
export type AbandonInterviewAction = { type: "abandonInterview" };
export type ConfirmAbandonInterviewAction = {
  type: "confirmAbandonInterview";
  reallyAbandon: boolean;
};

export type EngineAction =
  | StartInterviewAction
  | ChangeInterviewNameAction
  | AnswerQuestionAction
  | AbandonInterviewAction
  | ConfirmAbandonInterviewAction;

export interface AnswerQuestionActionParams {
  topicId: string;
  questionId: string;
  choiceId: string;
}

const initialState: State = {
  interviewStarted: false,
  abandonInterviewRequested: false
};
export { initialState };

export function engine(state: State, action: EngineAction): State {
  switch (action.type) {
    case "startInterview":
      return reducers.startInterview(state, action);

    case "changeInterviewName":
      return reducers.changeInterviewName(state, action);

    case "answerQuestion":
      return reducers.answerQuestion(state, action);

    case "abandonInterview":
      return reducers.abandonInterview(state, action);

    case "confirmAbandonInterview":
      return reducers.confirmAbandonInterview(state, action);

    default:
      return state;
  }
}

export const INTERVIEW_LOCAL_STORAGE_KEY = "INTERVIEW";

export default function useInterview() {
  const [interviewState, dispatch] = useReducer(engine, initialState);

  const startInterview = (interview: Interview) =>
    dispatch({ type: "startInterview", interview });

  const changeInterviewName = (interviewName: string) =>
    dispatch({ type: "changeInterviewName", interviewName });

  const answerQuestion = (answer: AnswerQuestionActionParams) =>
    dispatch({ type: "answerQuestion", actionParams: answer });

  const abandonInterview = () => dispatch({ type: "abandonInterview" });

  const confirmAbandonInterview = (reallyAbandon: boolean) =>
    dispatch({ type: "confirmAbandonInterview", reallyAbandon });

  useEffect(() => {
    const storedState = getFromLocalStorage<State>(INTERVIEW_LOCAL_STORAGE_KEY);
    if (storedState && storedState.interview) {
      startInterview(storedState.interview);
    }
  }, []);

  return {
    interviewState,
    answerQuestion,
    changeInterviewName,
    startInterview,
    abandonInterview,
    confirmAbandonInterview
  };
}
